import { useGetOne, useRecordContext, type RaRecord } from "react-admin";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { bookingRequesterDiffersFromOrganiser } from "@/utils/resolve-booking-identity-display";
import { formatListDate } from "@/utils/date-format";

export const HallBookingsShowHeader = () => {
  const record = useRecordContext<RaRecord>();
  const linkedRazaRequestId =
    record?.razaRequestId != null && String(record.razaRequestId).trim() !== ""
      ? String(record.razaRequestId)
      : "";
  const { data: linkedRazaRequest, isLoading: razaStatusLoading } = useGetOne(
    "razaRequests",
    { id: linkedRazaRequestId },
    { enabled: Boolean(linkedRazaRequestId) }
  );
  if (!record) return null;

  const bookingNo = typeof record.bookingNo === "string" ? record.bookingNo : "—";
  const organiser =
    typeof record.organiser === "string" && record.organiser.trim()
      ? record.organiser.trim()
      : "Hall booking";
  const itsNo = typeof record.itsNo === "string" ? record.itsNo : "—";
  const requesterName = typeof record.requesterName === "string" ? record.requesterName.trim() : "";
  const requesterItsNo =
    typeof record.requesterItsNo === "string" ? record.requesterItsNo.trim() : "";
  const showRequester = bookingRequesterDiffersFromOrganiser({
    organiser: record.organiser as string | null | undefined,
    itsNo: record.itsNo as string | null | undefined,
    requesterName: record.requesterName as string | null | undefined,
    requesterItsNo: record.requesterItsNo as string | null | undefined,
  });
  const checkedOut = Boolean(record.checkedOutOn);
  const linkedRazaGranted = Boolean(linkedRazaRequest?.razaGranted);

  return (
    <Box
      sx={{
        px: 2,
        pt: 2,
        pb: 1.5,
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="h2" noWrap>
            {organiser}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Booking #{bookingNo}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ·
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ITS {itsNo}
            </Typography>
          </Stack>
          {showRequester ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Raza requester: {requesterName || "—"}
              {requesterItsNo ? ` · ITS ${requesterItsNo}` : ""}
            </Typography>
          ) : null}
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Chip
            size="small"
            label={
              !linkedRazaRequestId
                ? "No raza linked"
                : razaStatusLoading
                  ? "Raza status..."
                  : linkedRazaGranted
                    ? "Raza granted"
                    : "Raza pending"
            }
            color={!linkedRazaRequestId ? "default" : linkedRazaGranted ? "success" : "warning"}
            variant={!linkedRazaRequestId ? "filled" : linkedRazaGranted ? "filled" : "outlined"}
          />
          <Chip
            size="small"
            label={checkedOut ? "Event closed" : "Event open"}
            color={checkedOut ? "default" : "primary"}
            variant={checkedOut ? "filled" : "outlined"}
          />
          {checkedOut ? (
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
              {formatListDate(record.checkedOutOn as string | Date, { empty: "" })}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
};

export default HallBookingsShowHeader;
